import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '@bnnvara/accounts';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer } from 'react';

import { MarkAllNotificationsAsRead } from '../../api/mutations/notifications.gql';
import { FetchNotificationsHistory } from '../../api/queries/me/notifications.gql';
import withApolloClient from '../../hocs/withApolloClient';
import { NotificationsContext } from './NotificationsContext';
import { reducer } from './reducer';
import { useNotificationsWebsocket } from './useNotificationsWebsocket';

/**
 * The data retrieved in the NotificationsProvider comes from:
 * 1.) BFF to retrieve the last notifications of a user. This is executed only once.
 * 2.) AWS websocket to constantly check for an individual notification.
 * The notifications of both sources are included in one state: notificationsHistory
 */
export const NotificationsProvider = withApolloClient(({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    groupedNotifications: [],
    notifications: [],
    lastUpdate: Date.now(),
    unreadCount: 0,
  });
  const websocketNotification = useNotificationsWebsocket();
  const {
    profile: { UID },
  } = useAccount();
  const [markAllNotificationsAsReadMutation] = useMutation(
    MarkAllNotificationsAsRead
  );
  const { data, loading: isLoading } = useQuery(FetchNotificationsHistory, {
    variables: {
      UID,
      skip: !UID,
    },
  });

  function updateNotifications(notifications) {
    dispatch({
      type: 'UPDATE_NOTIFICATIONS',
      payload: notifications,
    });
  }

  function addNotification(notification) {
    dispatch({ type: 'ADD_NOTIFICATIONS', payload: notification });
  }

  async function setNotificationsOnRead() {
    const old = [...state.notifications];

    updateNotifications(
      state.notifications.map((notification) => ({
        ...notification,
        isRead: true,
      }))
    );

    const result = await markAllNotificationsAsReadMutation({
      variables: { UID },
    });

    /**
     * In case the query is not executed successful the previous state is retrieved.
     * Apollo's build in optimistic response is not used for this,
     * because the notifications are not only retrieved with apollo,
     * but also with direct AWS websocket connection.
     */
    if (result.data.markAllNotificationsAsRead !== true) {
      updateNotifications(old);
    }
  }

  /**
   * Add notifications from graphql
   */
  useEffect(() => {
    if (data?.me?.notifications) {
      addNotification(data.me.notifications);
    }
  }, [data]);

  /**
   * Add notification from websocket
   */
  useEffect(() => {
    if (websocketNotification) {
      addNotification(websocketNotification);
    }
  }, [websocketNotification]);

  return (
    <NotificationsContext.Provider
      value={{
        isLoading,
        notifications: state.notifications,
        groupedNotifications: state.groupedNotifications,
        unreadCount: state.unreadCount,
        lastUpdate: state.lastUpdate,
        addNotification,
        setNotificationsOnRead,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
});

NotificationsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
